import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const CustomerSignup = () => {
  const [formData, setFormData] = useState({
    name: '',
    customer_id: '',
    email: '',
    country_code: '+91',
    mobile: '',
    date_of_birth: '',
    address: '',
    password: '',
    confirm_password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [passwordMatch, setPasswordMatch] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState({
    minLength: false,
    uppercase: false,
    lowercase: false,
    number: false,
    specialChar: false,
    noSpaces: false,
    notCommon: false
  });
  const navigate = useNavigate();

  const countryCodes = [
    { code: '+91', country: 'India', flag: '🇮🇳' },
    { code: '+1', country: 'USA', flag: '🇺🇸' },
    { code: '+44', country: 'UK', flag: '🇬🇧' },
    { code: '+61', country: 'Australia', flag: '🇦🇺' },
    { code: '+86', country: 'China', flag: '🇨🇳' },
    { code: '+81', country: 'Japan', flag: '🇯🇵' },
    { code: '+33', country: 'France', flag: '🇫🇷' },
    { code: '+49', country: 'Germany', flag: '🇩🇪' },
    { code: '+39', country: 'Italy', flag: '🇮🇹' },
    { code: '+34', country: 'Spain', flag: '🇪🇸' },
    { code: '+55', country: 'Brazil', flag: '🇧🇷' },
    { code: '+27', country: 'South Africa', flag: '🇿🇦' },
    { code: '+65', country: 'Singapore', flag: '🇸🇬' },
    { code: '+60', country: 'Malaysia', flag: '🇲🇾' },
    { code: '+66', country: 'Thailand', flag: '🇹🇭' },
    { code: '+62', country: 'Indonesia', flag: '🇮🇩' },
    { code: '+63', country: 'Philippines', flag: '🇵🇭' },
    { code: '+64', country: 'New Zealand', flag: '🇳🇿' },
    { code: '+1', country: 'Canada', flag: '🇨🇦' },
    { code: '+52', country: 'Mexico', flag: '🇲🇽' }
  ];

  const commonPasswords = ['123456', 'password', '12345678', 'qwerty', 'abc123', 'password123', '111111', '123123'];

  const validatePassword = (pwd) => {
    const validation = {
      minLength: pwd.length >= 8,
      uppercase: /[A-Z]/.test(pwd),
      lowercase: /[a-z]/.test(pwd),
      number: /[0-9]/.test(pwd),
      specialChar: /[!@#$%^&*]/.test(pwd),
      noSpaces: !/\s/.test(pwd),
      notCommon: !commonPasswords.includes(pwd.toLowerCase())
    };
    return validation;
  };

  const isPasswordValid = (pwd) => {
    const validation = validatePassword(pwd);
    return Object.values(validation).every(v => v === true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    if (name === 'password') {
      setPasswordValidation(validatePassword(value));
      if (formData.confirm_password) {
        setPasswordMatch(value === formData.confirm_password);
      }
    }
    
    if (name === 'confirm_password') {
      setPasswordMatch(value === formData.password || value === '');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch('https://infrec.in/php-backend/backend/user/customerregister.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          customer_id: formData.customer_id,
          email: formData.email,
          country_code: formData.country_code,
          mobile: formData.mobile,
          date_of_birth: formData.date_of_birth,
          address: formData.address,
          password: formData.password,
          confirm_password: formData.confirm_password
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess('Registration successful! Redirecting to login...');
        setTimeout(() => {
          navigate('/customer-login');
        }, 2000);
      } else {
        setError(data.message || 'Registration failed');
      }
    } catch (error) {
      console.error('Registration Error:', error);
      setError(`Error during registration: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 py-8">
      <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-2xl my-4">
        <h2 className="text-3xl font-bold text-center mb-6 text-darkBlue">Create Account</h2>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-red-800"><strong>Error:</strong> {error}</p>
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-green-800"><strong>Success:</strong> {success}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Row 1: Name and Customer ID */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 font-medium mb-2">Full Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter your full name"
                required
              />
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-2">Customer ID</label>
              <input
                type="text"
                name="customer_id"
                value={formData.customer_id}
                onChange={handleChange}
                className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter your customer id"
                required
              />
            </div>
          </div>

          {/* Row 2: Email and Mobile */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 font-medium mb-2">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter your email"
                required
              />
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-2">Mobile Number</label>
              <div className="flex gap-2">
                <select
                  name="country_code"
                  value={formData.country_code}
                  onChange={handleChange}
                  className="w-24 p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  {countryCodes.map((item) => (
                    <option key={item.code + item.country} value={item.code}>
                      {item.flag} {item.code}
                    </option>
                  ))}
                </select>
                <input
                  type="tel"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  className="flex-1 p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter mobile number"
                  maxLength="15"
                  required
                />
              </div>
            </div>
          </div>

          {/* Row 3: Date of Birth and Address */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 font-medium mb-2">Date of Birth</label>
              <input
                type="date"
                name="date_of_birth"
                value={formData.date_of_birth}
                onChange={handleChange}
                className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-2">Address</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter your address"
                required
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="block text-gray-700 font-medium mb-2">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 pr-16"
                placeholder="Enter password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-3 text-sm text-blue-600 hover:text-blue-800 font-semibold"
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>

            {formData.password && (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-xs font-semibold text-gray-700 mb-2">Password Requirements:</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className={passwordValidation.minLength ? 'text-green-600' : 'text-gray-500'}>
                    {passwordValidation.minLength ? '✅' : '❌'} Min 8 characters
                  </div>
                  <div className={passwordValidation.uppercase ? 'text-green-600' : 'text-gray-500'}>
                    {passwordValidation.uppercase ? '✅' : '❌'} Uppercase (A–Z)
                  </div>
                  <div className={passwordValidation.lowercase ? 'text-green-600' : 'text-gray-500'}>
                    {passwordValidation.lowercase ? '✅' : '❌'} Lowercase (a–z)
                  </div>
                  <div className={passwordValidation.number ? 'text-green-600' : 'text-gray-500'}>
                    {passwordValidation.number ? '✅' : '❌'} Number (0–9)
                  </div>
                  <div className={passwordValidation.specialChar ? 'text-green-600' : 'text-gray-500'}>
                    {passwordValidation.specialChar ? '✅' : '❌'} Special char (!@#$%^&*)
                  </div>
                  <div className={passwordValidation.noSpaces ? 'text-green-600' : 'text-gray-500'}>
                    {passwordValidation.noSpaces ? '✅' : '❌'} No spaces
                  </div>
                  <div className={passwordValidation.notCommon ? 'text-green-600' : 'text-gray-500'} colSpan="2">
                    {passwordValidation.notCommon ? '✅' : '❌'} Not a common password
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-gray-700 font-medium mb-2">Confirm Password</label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirm_password"
                value={formData.confirm_password}
                onChange={handleChange}
                className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 pr-16 ${
                  formData.confirm_password && !passwordMatch
                    ? 'border-red-500 focus:ring-red-500'
                    : formData.confirm_password && passwordMatch
                    ? 'border-green-500 focus:ring-green-500'
                    : 'focus:ring-blue-500'
                }`}
                placeholder="Confirm your password"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-3 text-sm text-blue-600 hover:text-blue-800 font-semibold"
              >
                {showConfirmPassword ? 'Hide' : 'Show'}
              </button>
            </div>
            {formData.confirm_password && !passwordMatch && (
              <p className="text-red-500 text-sm mt-1">❌ Passwords do not match</p>
            )}
            {formData.confirm_password && passwordMatch && formData.password && (
              <p className="text-green-500 text-sm mt-1">✅ Passwords match</p>
            )}
          </div>

          <div className="flex gap-2 justify-center pt-2">
            <button
              type="submit"
              disabled={loading || !passwordMatch || !isPasswordValid(formData.password)}
              className="flex-1 max-w-xs bg-darkBlue text-white p-3 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400"
            >
              {loading ? 'Creating Account...' : 'Sign Up'}
            </button>
            <button
              type="button"
              onClick={() => setFormData({
                name: '',
                customer_id: '',
                email: '',
                country_code: '+91',
                mobile: '',
                date_of_birth: '',
                address: '',
                password: '',
                confirm_password: ''
              })}
              disabled={loading}
              className="flex-1 max-w-xs bg-gray-600 text-white p-3 rounded-lg font-semibold hover:bg-gray-700 disabled:bg-gray-400"
            >
              Clear
            </button>
          </div>
        </form>

        <p className="text-center mt-4 text-gray-600">
          Already have an account? <a href="/customer-login" className="text-blue-600 hover:underline">Login</a>
        </p>
      </div>
    </div>
  );
};

export default CustomerSignup;
